import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Inbox, Send, Plus, Reply, CheckCircle2, XCircle, Archive, ArrowRight, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import VoiceInput from "@/components/VoiceInput";

const prioColors = { bassa:"bg-slate-100 text-slate-600", normale:"bg-blue-50 text-blue-700", alta:"bg-orange-100 text-orange-700", urgente:"bg-red-100 text-red-700" };
const typeLabel = { informativa:"Informativa", richiesta_decisione:"Richiesta decisione", richiesta_integrazione:"Richiesta integrazione", sollecito:"Sollecito", delega:"Delega" };

function ComposeDialog({ users, practices, onSent, initial }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    subject: initial?.subject || "", body: initial?.body || "",
    recipient_ids: initial?.recipient_ids || [], project_id: initial?.project_id || "_none",
    memo_type: "informativa", priority: "normale",
  });

  const toggleRecipient = (id) => {
    setForm(f => ({ ...f, recipient_ids: f.recipient_ids.includes(id) ? f.recipient_ids.filter(x=>x!==id) : [...f.recipient_ids, id] }));
  };

  const submit = async () => {
    if (!form.subject.trim() || !form.body.trim() || form.recipient_ids.length === 0) { toast.error("Oggetto, corpo e destinatari richiesti"); return; }
    try {
      await api.post("/memos", { ...form, project_id: form.project_id === "_none" ? null : form.project_id });
      toast.success("Memo inviato");
      setOpen(false);
      setForm({ subject:"", body:"", recipient_ids:[], project_id:"_none", memo_type:"informativa", priority:"normale" });
      onSent();
    } catch { toast.error("Errore invio memo"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-navy-800 hover:bg-navy-900 text-white" data-testid="new-memo-btn"><Plus className="w-4 h-4 mr-1" />Nuovo memo</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Nuovo memo</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Oggetto *</Label><Input value={form.subject} onChange={(e)=>setForm({...form, subject: e.target.value})} data-testid="memo-subject-input"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tipo memo</Label>
              <Select value={form.memo_type} onValueChange={(v)=>setForm({...form, memo_type: v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{Object.entries(typeLabel).map(([k,l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Priorità</Label>
              <Select value={form.priority} onValueChange={(v)=>setForm({...form, priority: v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normale">Normale</SelectItem><SelectItem value="alta">Alta</SelectItem><SelectItem value="urgente">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Pratica collegata (opzionale)</Label>
            <Select value={form.project_id} onValueChange={(v)=>setForm({...form, project_id: v})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— Memo generale di Studio —</SelectItem>
                {practices.map(p => <SelectItem key={p.id} value={p.id}>{p.numero_protocollo} · {p.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Destinatari *</Label>
            <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-md p-2 space-y-1">
              {users.map(u => (
                <label key={u.id} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded cursor-pointer">
                  <Checkbox checked={form.recipient_ids.includes(u.id)} onCheckedChange={()=>toggleRecipient(u.id)} />
                  <span className="text-sm">{u.full_name}</span>
                  <Badge variant="outline" className="ml-auto text-[10px]">{u.role.replace("_"," ")}</Badge>
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-1.5"><Label>Corpo *</Label>
            <div className="relative">
              <Textarea rows={5} value={form.body} onChange={(e)=>setForm({...form, body: e.target.value})} data-testid="memo-body-input" />
              <div className="absolute top-2 right-2">
                <VoiceInput onTranscript={(t)=>setForm(f=>({...f, body: (f.body ? f.body + " " : "") + t}))} />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Annulla</Button>
          <Button className="bg-navy-800 hover:bg-navy-900" onClick={submit} data-testid="memo-send-btn"><Send className="w-4 h-4 mr-1" />Invia</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MemoDetailDialog({ memo, onClose, onAction }) {
  const [response, setResponse] = useState("");
  if (!memo) return null;
  return (
    <Dialog open={!!memo} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span>{memo.subject}</span>
            <Badge className={`${prioColors[memo.priority]} border-0 text-[10px]`}>{memo.priority}</Badge>
            <Badge variant="outline" className="text-[10px]">{typeLabel[memo.memo_type]}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-xs text-slate-500">Da <strong className="text-slate-700">{memo.sender_name}</strong> · {new Date(memo.created_at).toLocaleString("it-IT")} {memo.project_protocol && <>· <span className="font-mono">{memo.project_protocol}</span></>}</div>
          <div className="text-slate-800 whitespace-pre-line p-4 bg-slate-50 rounded-md">{memo.body}</div>
          <Textarea rows={3} placeholder="Scrivi la tua risposta (opzionale)" value={response} onChange={(e)=>setResponse(e.target.value)} data-testid="memo-response-input" />
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={()=>{ onAction(memo, "archive"); onClose(); }} data-testid="action-archive"><Archive className="w-4 h-4 mr-1" />Archivia</Button>
          <Button variant="outline" className="border-red-200 text-red-600" onClick={()=>{ onAction(memo, "reject", response); onClose(); }} data-testid="action-reject"><XCircle className="w-4 h-4 mr-1" />Rifiuta</Button>
          <Button variant="outline" className="border-emerald-200 text-emerald-700" onClick={()=>{ onAction(memo, "accept", response); onClose(); }} data-testid="action-accept"><CheckCircle2 className="w-4 h-4 mr-1" />Accetta</Button>
          <Button className="bg-navy-800 hover:bg-navy-900" onClick={()=>{ onAction(memo, "respond", response); onClose(); }} data-testid="action-respond"><Reply className="w-4 h-4 mr-1" />Rispondi</Button>
          {memo.project_id && <Button variant="outline" onClick={()=>{ onAction(memo, "convert_task", response); onClose(); }}><ArrowRight className="w-4 h-4 mr-1" />Converti in Task</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Memo() {
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [users, setUsers] = useState([]);
  const [practices, setPractices] = useState([]);
  const [tab, setTab] = useState("inbox");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("_all");
  const [selectedMemo, setSelectedMemo] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    const [i, s, u, p] = await Promise.all([
      api.get("/memos/inbox" + (q ? `?q=${encodeURIComponent(q)}` : "")),
      api.get("/memos/sent" + (q ? `?q=${encodeURIComponent(q)}` : "")),
      api.get("/users"),
      api.get("/practices"),
    ]);
    setInbox(i.data); setSent(s.data); setUsers(u.data); setPractices(p.data);
  };
  useEffect(() => { load(); }, [q]);

  const openMemo = async (m) => {
    if (m.my_status === "non_letto") { try { await api.post(`/memos/${m.id}/read`); } catch {} }
    setSelectedMemo(m);
    load();
  };

  const doAction = async (m, action, response) => {
    try { await api.post(`/memos/${m.id}/action`, { action, response_text: response || null }); toast.success("Aggiornato"); load(); }
    catch { toast.error("Errore"); }
  };

  const filteredInbox = inbox.filter(m => statusFilter === "_all" || m.my_status === statusFilter);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy-900">Memo</h1>
          <p className="text-sm text-slate-500 mt-1">Comunicazioni operative tracciate e auditate</p>
        </div>
        <ComposeDialog users={users} practices={practices} onSent={load} />
      </div>

      <Card className="p-4">
        <div className="grid md:grid-cols-2 gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input className="pl-9" placeholder="Cerca per oggetto, testo, mittente" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="memo-search-input"/>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="memo-status-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Tutti gli stati</SelectItem>
              <SelectItem value="non_letto">Non letti</SelectItem>
              <SelectItem value="letto">Letti</SelectItem>
              <SelectItem value="risposto">Risposti</SelectItem>
              <SelectItem value="accettato">Accettati</SelectItem>
              <SelectItem value="rifiutato">Rifiutati</SelectItem>
              <SelectItem value="archiviato">Archiviati</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="inbox" data-testid="tab-inbox"><Inbox className="w-4 h-4 mr-1.5" />Ricevuti ({inbox.length})</TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-sent"><Send className="w-4 h-4 mr-1.5" />Inviati ({sent.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="inbox">
          <div className="space-y-2.5">
            {filteredInbox.length === 0 && <Card className="p-10 text-center text-slate-400">Nessun memo</Card>}
            {filteredInbox.map(m => {
              const unread = m.my_status === "non_letto";
              return (
                <Card key={m.id} className={`p-4 hover:shadow-sm cursor-pointer ${unread ? "border-amber-300 bg-amber-50/40" : ""}`} onClick={()=>openMemo(m)} data-testid={`memo-inbox-row-${m.id}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    {unread && <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-sm ${unread ? "font-bold text-navy-900" : "text-slate-700"}`}>{m.subject}</span>
                        <Badge className={`${prioColors[m.priority]} border-0 text-[10px]`}>{m.priority}</Badge>
                        <Badge variant="outline" className="text-[10px]">{typeLabel[m.memo_type]}</Badge>
                        <Badge variant="outline" className="text-[10px] capitalize">{m.my_status?.replace("_"," ")}</Badge>
                      </div>
                      <div className="text-xs text-slate-500">
                        Da <strong className="text-slate-700">{m.sender_name}</strong> · {new Date(m.created_at).toLocaleString("it-IT")} {m.project_protocol && <>· <span className="font-mono">{m.project_protocol}</span></>}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </TabsContent>
        <TabsContent value="sent">
          <div className="space-y-2.5">
            {sent.length === 0 && <Card className="p-10 text-center text-slate-400">Nessun memo inviato</Card>}
            {sent.map(m => (
              <Card key={m.id} className="p-4" data-testid={`memo-sent-row-${m.id}`}>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="text-sm font-medium text-navy-900">{m.subject}</span>
                  <Badge className={`${prioColors[m.priority]} border-0 text-[10px]`}>{m.priority}</Badge>
                  <Badge variant="outline" className="text-[10px]">{typeLabel[m.memo_type]}</Badge>
                  {m.fully_processed && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">processato</Badge>}
                  <span className="ml-auto text-xs text-slate-500">{new Date(m.created_at).toLocaleString("it-IT")}</span>
                </div>
                <div className="text-sm text-slate-600 line-clamp-2 mb-2">{m.body}</div>
                <div className="flex flex-wrap gap-1.5">
                  {m.recipients_meta.map((r, i) => (
                    <div key={i} className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-slate-100">
                      <span>{r.user_name}</span>
                      <span className={`w-1.5 h-1.5 rounded-full ${r.status === "non_letto" ? "bg-amber-500" : r.status === "letto" ? "bg-blue-500" : r.status === "accettato" ? "bg-emerald-500" : r.status === "rifiutato" ? "bg-red-500" : "bg-slate-400"}`} />
                      <span className="text-slate-500">{r.status.replace("_"," ")}</span>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <MemoDetailDialog memo={selectedMemo} onClose={()=>setSelectedMemo(null)} onAction={doAction} />
    </div>
  );
}
